import { readStorage, writeStorage } from "../environment";
import * as logger from "../logger";
import type { TrackedEvent } from "../types";

const QUEUE_KEY = "business-analytics.queue";

/**
 * Pending events, mirrored to sessionStorage so a reload or a tab crash does not lose them.
 *
 * sessionStorage rather than localStorage: the session model is per-tab, and a shared
 * localStorage key would have two tabs racing on read-modify-write with no locking, one of
 * them flushing events stamped with the other's sessionId.
 */
export class EventQueue {
	private readonly maxSize: number;
	private events: TrackedEvent[] = [];

	constructor(maxSize: number) {
		this.maxSize = maxSize;
	}

	get size(): number {
		return this.events.length;
	}

	/** Picks up whatever a previous page load left behind. Malformed state is discarded. */
	restore(): void {
		const raw = readStorage("session", QUEUE_KEY);
		if (!raw) return;

		try {
			const parsed: unknown = JSON.parse(raw);
			this.events = Array.isArray(parsed) ? (parsed as TrackedEvent[]) : [];
			if (this.events.length > 0) {
				logger.log(`restored ${this.events.length} event(s) from a previous page load`);
			}
		} catch {
			this.events = [];
		}
	}

	push(event: TrackedEvent): void {
		if (this.events.length >= this.maxSize) {
			this.events.shift();
			logger.warn(`queue is full (${this.maxSize}); dropped the oldest event`);
		}
		this.events.push(event);
		this.persist();
	}

	/** Removes and returns up to `size` events, oldest first. */
	take(size: number): TrackedEvent[] {
		const batch = this.events.slice(0, size);
		this.events = this.events.slice(batch.length);
		this.persist();
		return batch;
	}

	/** Puts a failed batch back at the head, so ordering of what is left is preserved. */
	requeue(events: TrackedEvent[]): void {
		this.events = [...events, ...this.events].slice(-this.maxSize);
		this.persist();
	}

	private persist(): void {
		writeStorage("session", QUEUE_KEY, JSON.stringify(this.events));
	}
}
